/**
 * Centralized query key factory for TanStack Query.
 *
 * Every useQuery / invalidateQueries call should reference a key from this
 * factory so that cache invalidation is consistent and discoverable.
 *
 * Convention:
 *   - `all`   — broadest prefix for bulk invalidation
 *   - `list`  — filtered / paginated lists
 *   - `detail`— single-entity fetch
 *   - factory methods return `as const` tuples
 *
 * Parameter types are intentionally loose (accepting undefined/null) because
 * callers frequently pass values from useParams() or nullable state. The
 * queries themselves gate execution with `enabled:` guards.
 */

/** Shorthand for the typical ID parameter sourced from useParams / state. */
type Id = number | string | undefined | null;

export const queryKeys = {
  patients: {
    all: ['patients'] as const,
    list: (params: Record<string, unknown>) => ['patients', 'list', params] as const,
    detail: (id: Id) => ['patients', 'detail', id == null ? id : String(id)] as const,
    quizzes: (patientId: Id) => ['patients', 'quizzes', patientId] as const,
    dashboard: () => ['patients', 'dashboard'] as const,
    adminList: (params: Record<string, unknown>) => ['patients', 'admin-list', params] as const,
    mergeSearch: (role: string, search: string) =>
      ['patients', 'merge-search', role, search] as const,
    mergePreview: (primaryId: Id, duplicateId: Id, revision: number) =>
      ['patients', 'merge-preview', primaryId, duplicateId, revision] as const,
    adminQueue: (params: Record<string, unknown>) => ['patients', 'admin-queue', params] as const,
    adminDashboard: () => ['patients', 'admin-dashboard'] as const,
  },
  labResults: {
    all: ['lab-results'] as const,
    detail: (id: Id) => ['lab-results', 'detail', id] as const,
    file: (id: Id) => ['lab-results', 'file', id] as const,
    pdf: (id: Id) => ['lab-results', 'pdf', id] as const,
  },
  labTests: {
    all: ['lab-tests'] as const,
    // Prefix key for invalidating both includeInactive variants at once
    reportedValues: (labTestId: Id) => ['lab-tests', 'reported-values', labTestId] as const,
    reportedValuesList: (labTestId: Id, includeInactive: boolean) =>
      ['lab-tests', 'reported-values', labTestId, includeInactive] as const,
    // Prefix key for invalidating both includeInactive variants at once
    markers: (labTestId: Id) => ['lab-tests', 'markers', labTestId] as const,
    markersList: (labTestId: Id, includeInactive: boolean) =>
      ['lab-tests', 'markers', labTestId, includeInactive] as const,
  },
  patterns: {
    all: ['patterns'] as const,
    solve: (labResultId: Id) => ['patterns', 'solve', labResultId] as const,
    possible: (labResultId: Id) => ['patterns', 'possible', labResultId] as const,
    listAll: () => ['patterns', 'all'] as const,
    byType: (typeId: Id, includeInactive = false) =>
      ['patterns', 'byType', typeId, includeInactive] as const,
    hormones: (patternId: Id) => ['patterns', 'hormones', patternId] as const,
    types: () => ['patterns', 'types'] as const,
    comments: () => ['patterns', 'comments'] as const,
    eligibility: () => ['patterns', 'eligibility'] as const,
    matchCriteria: (patternId: Id) => ['patterns', 'match-criteria', patternId] as const,
    matchDefinition: (patternId: Id, includeInactive = true) =>
      ['patterns', 'match-definition', patternId, { includeInactive }] as const,
    // Prefix key for invalidating both includeInactive variants at once
    templateHormones: (patternTypeId: Id) =>
      ['patterns', 'template-hormones', patternTypeId] as const,
    templateHormonesList: (patternTypeId: Id, includeInactive: boolean) =>
      ['patterns', 'template-hormones', patternTypeId, includeInactive] as const,
    // Prefix key for invalidating both includeInactivePatterns variants at once
    templateCoverage: (patternTypeId: Id) =>
      ['patterns', 'template-coverage', patternTypeId] as const,
    templateCoverageList: (patternTypeId: Id, includeInactivePatterns: boolean) =>
      ['patterns', 'template-coverage', patternTypeId, includeInactivePatterns] as const,
  },
  modifiers: {
    all: ['modifiers'] as const,
    byLabResult: (labResultId: Id) => ['modifiers', 'byLabResult', labResultId] as const,
    detail: (id: Id) => ['modifiers', 'detail', id] as const,
    comments: () => ['modifiers', 'comments'] as const,
    dependents: (id: Id) => ['modifiers', 'dependents', id] as const,
    list: () => ['modifiers', 'list'] as const,
  },
  products: {
    all: ['products'] as const,
    byLabResult: (labResultId: Id) => ['products', 'byLabResult', labResultId] as const,
    listAll: () => ['products', 'all'] as const,
  },
  productCatalog: {
    all: ['product-catalog'] as const,
    products: (params: Record<string, unknown>) => ['product-catalog', 'products', params] as const,
    vendors: () => ['product-catalog', 'vendors'] as const,
    categories: () => ['product-catalog', 'categories'] as const,
    fulfillmentPartners: () => ['product-catalog', 'fulfillment-partners'] as const,
  },
  providerCart: {
    all: ['provider-cart'] as const,
    products: () => ['provider-cart', 'products'] as const,
  },
  hormones: {
    all: ['hormones'] as const,
    commentsByLabResult: (labResultId: Id) =>
      ['hormones', 'commentsByLabResult', labResultId] as const,
    comments: () => ['hormones', 'comments'] as const,
    names: () => ['hormones', 'names'] as const,
    levelsAll: () => ['hormones', 'levels-all'] as const,
    adjustments: () => ['hormones', 'adjustments'] as const,
    triggers: (commentId: Id) => ['hormones', 'triggers', commentId] as const,
  },
  symptoms: {
    all: ['symptoms'] as const,
    byLabResult: (labResultId: Id, patientId: Id) =>
      ['symptoms', 'byLabResult', labResultId, patientId] as const,
  },
  profile: {
    all: ['profile'] as const,
    detail: (uid?: string) => ['profile', 'detail', uid] as const,
    user: () => ['profile', 'user'] as const,
  },
  referenceData: {
    all: ['reference-data'] as const,
    states: () => ['reference-data', 'states'] as const,
    practitionerTypes: () => ['reference-data', 'practitioner-types'] as const,
    specializations: () => ['reference-data', 'specializations'] as const,
    categories: () => ['reference-data', 'categories'] as const,
  },
  evexia: {
    all: ['evexia'] as const,
    connection: () => ['evexia', 'connection'] as const,
  },
  fullscript: {
    all: ['fullscript'] as const,
    auth: () => ['fullscript', 'auth'] as const,
    callback: (code: string | null) => ['fullscript', 'callback', code] as const,
  },
  shopify: {
    all: ['shopify'] as const,
    meSettings: () => ['shopify', 'me-settings'] as const,
    providerCartProducts: () => ['shopify', 'provider-cart-products'] as const,
    providerCartHistory: (take: number) => ['shopify', 'provider-cart-history', take] as const,
  },
  reports: {
    all: ['reports'] as const,
    lifestyle: (labResultId: Id, patientId: Id) =>
      ['reports', 'lifestyle', labResultId, patientId] as const,
    products: (labResultId: Id, patientId: Id) =>
      ['reports', 'products', labResultId, patientId] as const,
    hormone: (labResultId: Id, patientId: Id) =>
      ['reports', 'hormone', labResultId, patientId] as const,
  },
  comments: {
    all: ['comments'] as const,
    hormoneAdminLists: () => ['comments', 'hormone-admin-list'] as const,
    hormoneAdminList: (params: Record<string, unknown>) =>
      ['comments', 'hormone-admin-list', params] as const,
    searchAll: ['comments', 'search'] as const,
    search: (term: string) => ['comments', 'search', term] as const,
    questionTriggers: (commentId: Id) => ['comments', 'question-triggers', commentId] as const,
    hormoneTriggers: (commentId: Id) => ['comments', 'hormone-triggers', commentId] as const,
    allQuestions: () => ['comments', 'all-questions'] as const,
    levelNames: () => ['comments', 'level-names'] as const,
    patternReferenceData: () => ['comments', 'pattern-reference-data'] as const,
  },
  questions: {
    all: ['questions'] as const,
    bySex: (sex?: string | number) => ['questions', 'bySex', sex] as const,
  },
  axisMatrix: {
    all: ['axis-matrix'] as const,
    pendingApprovals: () => ['axis-matrix', 'pending-approvals'] as const,
    quizProductReport: (hash: string | undefined) =>
      ['axis-matrix', 'quiz-product-report', hash] as const,
  },
  /** Managed AxisMatrix quizzes (quiz engine v2). */
  managedQuizzes: {
    all: ['managed-quizzes'] as const,
    /** Prefix key for invalidating both includeInactive variants at once. */
    adminList: () => ['managed-quizzes', 'admin-list'] as const,
    adminListFiltered: (includeInactive: boolean) =>
      ['managed-quizzes', 'admin-list', includeInactive] as const,
    draft: (quizId: Id) => ['managed-quizzes', 'draft', quizId] as const,
    draftStatus: (quizId: Id) => ['managed-quizzes', 'draft-status', quizId] as const,
    versions: (quizId: Id) => ['managed-quizzes', 'versions', quizId] as const,
    version: (quizId: Id, versionId: Id) =>
      ['managed-quizzes', 'versions', quizId, versionId] as const,
    /** Prefix key for invalidating both includeRetired variants at once. */
    printables: (quizId: Id) => ['managed-quizzes', 'printables', quizId] as const,
    printablesFiltered: (quizId: Id, includeRetired: boolean) =>
      ['managed-quizzes', 'printables', quizId, includeRetired] as const,
    /**
     * Admin tester render definition. Keyed by source because draft and published
     * are different definitions of the same quiz and must not share a cache entry.
     */
    testDefinition: (quizId: Id, source: string) =>
      ['managed-quizzes', 'test-definition', quizId, source] as const,
    /** Public, render-only definition summaries. */
    definitions: () => ['managed-quizzes', 'definitions'] as const,
    /**
     * A render definition is identified by key plus whatever pins it — an explicit
     * version or an invitation hash — since the hash resolves to its own version.
     */
    definition: (
      quizKey: string | null | undefined,
      versionId?: number | null,
      hash?: string | null,
    ) => ['managed-quizzes', 'definition', quizKey, versionId ?? null, hash ?? null] as const,
  },
  practitioners: {
    all: ['practitioners'] as const,
    list: (params: Record<string, unknown>) => ['practitioners', 'list', params] as const,
    detail: (id: Id) => ['practitioners', 'detail', id] as const,
    forOverrides: () => ['practitioners', 'for-overrides'] as const,
  },
  adminUsers: {
    all: ['admin-users'] as const,
    list: (params: Record<string, unknown>) => ['admin-users', 'list', params] as const,
  },
  featureFlags: {
    all: ['feature-flags'] as const,
    list: () => ['feature-flags', 'list'] as const,
    resolved: () => ['feature-flags', 'resolved'] as const,
  },
  emailTemplates: {
    all: ['email-templates'] as const,
    list: () => ['email-templates', 'list'] as const,
    detail: (id: Id) => ['email-templates', 'detail', id] as const,
  },
  content: {
    all: ['content'] as const,
    post: (slug: string | undefined) => ['content', 'post', slug] as const,
    posts: (type: string, ...rest: unknown[]) => ['content', 'posts', type, ...rest] as const,
    categories: (type: string) => ['content', 'categories', type] as const,
    adminPostsPrefix: ['content', 'admin-posts'] as const,
    adminPosts: (params: Record<string, unknown>) => ['content', 'admin-posts', params] as const,
    adminPostsAllPrefix: ['content', 'admin-posts-all'] as const,
    adminPostsAll: (params: Record<string, unknown>) =>
      ['content', 'admin-posts-all', params] as const,
    adminCategories: (tab: string) => ['content', 'admin-categories', tab] as const,
    adminPost: (id: Id) => ['content', 'admin-post', id] as const,
    contactSubmissionsPrefix: ['content', 'contact-submissions'] as const,
    contactSubmissions: (params: Record<string, unknown>) =>
      ['content', 'contact-submissions', params] as const,
    latestNews: () => ['content', 'latest-news'] as const,
  },
  systemData: {
    all: ['system-data'] as const,
    report: (params: Record<string, unknown>) => ['system-data', 'report', params] as const,
  },
  telemetry: {
    all: ['telemetry'] as const,
    health: () => ['telemetry', 'health'] as const,
    email: (params: Record<string, unknown>) => ['telemetry', 'email', params] as const,
    emailEvents: (params: Record<string, unknown>) =>
      ['telemetry', 'email-events', params] as const,
    trace: (traceId: string | undefined) => ['telemetry', 'trace', traceId] as const,
  },
  sessions: {
    all: ['sessions'] as const,
    list: (params: Record<string, unknown>) => ['sessions', 'list', params] as const,
    detail: (id: Id) => ['sessions', 'detail', id] as const,
    events: (id: Id) => ['sessions', 'events', id] as const,
  },
  settings: {
    all: ['settings'] as const,
    admin: () => ['settings', 'admin'] as const,
    me: () => ['settings', 'me'] as const,
  },
  trace: {
    all: ['trace'] as const,
    byLabTestResult: (labTestResultId: Id) =>
      ['trace', 'byLabTestResult', labTestResultId] as const,
  },
  ruleEngine: {
    all: ['rule-engine'] as const,
    versions: () => ['rule-engine', 'versions'] as const,
    draftStatus: () => ['rule-engine', 'draft-status'] as const,
    referenceData: () => ['rule-engine', 'reference-data'] as const,
    ruleGroups: () => ['rule-engine', 'rule-groups'] as const,
    ruleGroupsForModifier: (modifierId: Id) =>
      ['rule-engine', 'rule-groups', 'modifier', modifierId] as const,
    ruleGroup: (groupId: Id) => ['rule-engine', 'rule-group', groupId] as const,
    priorityGroups: () => ['rule-engine', 'priority-groups'] as const,
    computedVariables: () => ['rule-engine', 'computed-variables'] as const,
    computedVariable: (id: Id) => ['rule-engine', 'computed-variable', id] as const,
    variableDependents: (id: Id) => ['rule-engine', 'variable-dependents', id] as const,
    commentRuleGroups: () => ['rule-engine', 'comment-rule-groups'] as const,
    commentRuleGroupsForComment: (commentId: Id) =>
      ['rule-engine', 'comment-rule-groups', 'comment', commentId] as const,
    contextualCommentRuleGroups: (contextType: string, contextId: Id) =>
      ['rule-engine', 'comment-rule-groups', 'contextual', contextType, contextId] as const,
    patternCommentSummaries: (patternId: Id, params?: Record<string, unknown>) =>
      params
        ? (['rule-engine', 'pattern-comment-summaries', patternId, params] as const)
        : (['rule-engine', 'pattern-comment-summaries', patternId] as const),
    reportInsightSubcategories: () => ['rule-engine', 'report-insight-subcategories'] as const,
    commentRuleGroup: (groupId: Id) => ['rule-engine', 'comment-rule-group', groupId] as const,
    commentRuleTemplates: () => ['rule-engine', 'comment-rule-templates'] as const,
    commentRuleTemplate: (templateId: Id) =>
      ['rule-engine', 'comment-rule-template', templateId] as const,
    commentRuleGroupDefaults: () => ['rule-engine', 'comment-rule-group-defaults'] as const,
    syntheticHormones: () => ['rule-engine', 'synthetic-hormones'] as const,
    syntheticHormone: (id: Id) => ['rule-engine', 'synthetic-hormone', id] as const,
    syntheticHormoneDependents: (id: Id) =>
      ['rule-engine', 'synthetic-hormone-dependents', id] as const,
    reviewRuleGroups: () => ['rule-engine', 'review-rule-groups'] as const,
    reviewRuleGroup: (groupId: Id) => ['rule-engine', 'review-rule-group', groupId] as const,
    patternProducts: (patternId: Id) => ['rule-engine', 'pattern-products', patternId] as const,
  },
} as const;
